import type { CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSiteMargin } from "@/Shared/Hooks/useAppSiteMargin";
import { useIntercom } from "react-use-intercom";
import { LightningIcon } from "@phosphor-icons/react/Lightning";
import { ListIcon } from "@phosphor-icons/react/List";
import { MoonIcon } from "@phosphor-icons/react/Moon";
import { SunIcon } from "@phosphor-icons/react/Sun";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { Menu } from "@freightsimple/ui-core/Menu/Menu";
import { useThemeContext } from "@freightsimple/ui-core/Theme/useThemeContext";
import { NavBarSignedOutMenuItems } from "./NavBarSignedOutMenuItems";
import { NavBarThemeMenuItems } from "../Menus/Internal/NavBarThemeMenuItems";

const HELP_CENTER_URL = "https://help.freightsimple.com";

// Public links share the inactive nav-item styling from NavBarTopBar so the
// signed-out header matches the signed-in one.
const linkClass =
  "rounded-md px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap text-content-secondary transition-colors hover:bg-muted hover:text-foreground";

/**
 * The signed-out header — the same shell as NavBarTopBar (scaled logo, bottom
 * border, matching link styling) with the public links (Help Center, Live Chat,
 * Sign In) and the get-quotes CTA. Shown to logged-out visitors.
 *
 * Below `md` the three links can't fit beside the logo (they need ~560px), so
 * they collapse into a hamburger menu — otherwise the whole page picks up a
 * horizontal scroll on a phone.
 */
export function NavBarSignedOut() {
  const location = useLocation();
  const navigate = useNavigate();
  const siteMargin = useAppSiteMargin();
  const gutter: CSSProperties = {
    paddingLeft: siteMargin,
    paddingRight: siteMargin,
  };
  const { show } = useIntercom();
  const { resolvedTheme } = useThemeContext();

  const showSignIn = !location.pathname.includes("sign-in");
  const showGetQuotes = ["/", "/create-account", "/sign-in"].includes(
    location.pathname,
  );

  const openHelpCenter = () => {
    window.open(HELP_CENTER_URL, "_blank", "noopener");
  };

  return (
    // Same measured gutter as NavBarTopBar and the routed content, so the logo
    // lines up with the page heading below it — see useAppSiteMargin.
    <nav
      className="flex h-14 items-center justify-between gap-4 border-b border-border bg-card"
      style={gutter}
    >
      <div className="origin-left scale-[0.8]">
        <Logo />
      </div>

      <div className="flex items-center gap-1">
        {/* Inline links — the desktop layout. Hidden on a phone, where they
            don't fit; the hamburger below takes over. */}
        <div className="hidden items-center gap-1 md:flex">
          <button type="button" onClick={openHelpCenter} className={linkClass}>
            Help Center
          </button>
          <button type="button" onClick={() => show()} className={linkClass}>
            Live Chat & Support
          </button>
          {showSignIn && (
            <button
              type="button"
              onClick={() => navigate("/sign-in")}
              className={linkClass}
            >
              Sign In
            </button>
          )}

          {/* The theme picker is in the user menu once you sign in, but the
              whole pre-signup funnel is signed out — and the default is
              `system`, so a visitor on OS dark lands here in dark with no way
              back. An icon rather than a fourth text link: it is a preference,
              not a destination. */}
          <Menu
            trigger={
              <button
                type="button"
                aria-label="Theme"
                className={`${linkClass} flex items-center`}
              >
                {resolvedTheme === "dark" ? (
                  <MoonIcon className="h-4 w-4" weight="fill" />
                ) : (
                  <SunIcon className="h-4 w-4" weight="fill" />
                )}
              </button>
            }
          >
            <NavBarThemeMenuItems />
          </Menu>
        </div>

        {showGetQuotes && (
          <div className="ml-2">
            <Button
              primary
              icon={LightningIcon}
              onClick={() => navigate("/book/details")}
            >
              Get Your First Quotes In Under A Minute!
            </Button>
          </div>
        )}

        {/* The same links as a hamburger menu on a phone. */}
        <div className="md:hidden">
          <Menu
            trigger={
              <button
                type="button"
                aria-label="Menu"
                className="flex items-center rounded-md p-1.5 text-content-secondary transition-colors hover:bg-muted hover:text-foreground"
              >
                <ListIcon className="h-5 w-5" weight="bold" />
              </button>
            }
          >
            <NavBarSignedOutMenuItems />
          </Menu>
        </div>
      </div>
    </nav>
  );
}
